<div class="bowling-game" [class.is-immersive]="isFullscreen" *ngIf="state">
  <div class="game-header">
    <span class="frame-label">
      {{ currLang === 'ar' ? 'الجولة' : 'Frame' }}
      <strong>{{ (state.currentFrame + 1) | arabicNumber:currLang }}</strong>
      <span class="of">/</span>
      <strong>{{ 10 | arabicNumber:currLang }}</strong>
    </span>
    <span class="game-title">🎳 {{ currLang === 'ar' ? 'مضمار البولينج' : 'Strike Lane' }}</span>
    <span class="header-bowler" *ngIf="currentStudent || state.mode === BowlingMode.ONE_VS_ONE">
      <app-student-avatar *ngIf="currentStudent" [student]="currentStudent" [size]="28" [active]="true"></app-student-avatar>
      <span class="header-bowler-name">{{ currentTurnLabel }}</span>
    </span>
  </div>

  <div class="scene-area">
    <app-bowling-scene
      [currLang]="currLang"
      [pinsStanding]="pinsStanding"
      [rackTick]="rackTick"
      [inputDisabled]="inputDisabled"
      [scoreboardData]="scoreboardData"
      (ready)="onSceneReady()"
      (rollStarted)="onRollStarted()"
      (rollComplete)="onRollComplete($event)">
    </app-bowling-scene>

    <!-- Scoreboards are overlaid on top of the scene with a translucent
         backdrop so the lane is still visible behind. Works for both 1v1
         (player names) and team modes (team names). -->
    <div class="top-scoreboard">
      <app-bowling-scorecard
        [participant]="state.participants[0]"
        [currentFrame]="state.currentSide === 0 ? state.currentFrame : -1"
        [isActive]="state.currentSide === 0"
        [currLang]="currLang">
      </app-bowling-scorecard>
      <app-bowling-scorecard
        [participant]="state.participants[1]"
        [currentFrame]="state.currentSide === 1 ? state.currentFrame : -1"
        [isActive]="state.currentSide === 1"
        [currLang]="currLang">
      </app-bowling-scorecard>
    </div>

    <!-- Big-number pin-count banner. Vertically centred over the
         scene; every roll triggers it (strike / gutter / numeric).
         Three decorative pin emojis float around the number; the
         label text + per-kind colour change with the banner kind. -->
    <div class="roll-banner" *ngIf="banner" [attr.data-kind]="banner">
      <div class="banner-pin-display">
        <span class="banner-pin banner-pin--tl" aria-hidden="true">🎳</span>
        <span class="banner-pin banner-pin--tr" aria-hidden="true">🎳</span>
        <span class="banner-pin banner-pin--br" aria-hidden="true">🎳</span>
        <span class="banner-number">
          {{ lastRollPins | arabicNumber:currLang }}
        </span>
      </div>
      <div class="banner-label">
        {{ banner === 'gutter'
          ? (currLang === 'ar' ? 'في المجرى' : 'GUTTER')
          : (currLang === 'ar' ? 'دبابيس' : 'PINS') }}
      </div>
      <div class="banner-sub" *ngIf="banner === 'strike'">
        {{ currLang === 'ar' ? 'ضربة قاضية!' : 'STRIKE!' }}
      </div>
    </div>

    <!-- (Bowler turn moved to .game-header — no longer overlaid on the scene.) -->

    <!-- Boxing-ring "NEXT UP" card — appears for 5 s after the
         pin-count banner clears, before the next bowler can roll.
         Dancer figure on the side "carries" a tilted card with the
         next bowler's name + team accent. -->
    <div class="next-bowler-overlay" *ngIf="showNextBowler">
      <div class="next-bowler-card" [style.--team-color]="currentSide?.color || '#FFD54A'">
        <div class="card-corner card-corner--tl" aria-hidden="true">★</div>
        <div class="card-corner card-corner--tr" aria-hidden="true">★</div>
        <div class="card-corner card-corner--bl" aria-hidden="true">★</div>
        <div class="card-corner card-corner--br" aria-hidden="true">★</div>

        <div class="card-label">
          {{ currLang === 'ar' ? 'الجولة التالية' : 'NEXT UP' }}
        </div>

        <div class="card-player">
          <app-student-avatar *ngIf="currentStudent"
                              [student]="currentStudent"
                              [size]="76"
                              [active]="true">
          </app-student-avatar>
          <div class="card-player-name">{{ currentStudentName }}</div>
        </div>
      </div>
    </div>

    <!-- How-to-play floating button — bottom-right of scene area, just
         under the power gauge. Labeled (localised), click to (re)open
         the onboarding overlay. -->
    <button class="how-to-play-btn"
            type="button"
            (click)="openHowToPlay()">
      <span aria-hidden="true" class="how-to-play-btn-icon">?</span>
      <span class="how-to-play-btn-text">
        {{ currLang === 'ar' ? 'كيفية اللعب' : 'How to Play' }}
      </span>
    </button>

    <!-- Fullscreen toggle — bottom-left of scene area, mirror of the
         How to Play button. Icon swaps based on current FS state. -->
    <button class="fullscreen-btn"
            type="button"
            [attr.aria-label]="isFullscreen
              ? (currLang === 'ar' ? 'إنهاء وضع ملء الشاشة' : 'Exit fullscreen')
              : (currLang === 'ar' ? 'وضع ملء الشاشة' : 'Enter fullscreen')"
            [title]="isFullscreen
              ? (currLang === 'ar' ? 'إنهاء ملء الشاشة' : 'Exit fullscreen')
              : (currLang === 'ar' ? 'ملء الشاشة' : 'Fullscreen')"
            (click)="toggleFullscreen()">
      <!-- Enter fullscreen: corner-out arrows -->
      <svg *ngIf="!isFullscreen" width="20" height="20" viewBox="0 0 24 24"
           fill="none" stroke="currentColor"
           stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M3 8V3h5"/>
        <path d="M16 3h5v5"/>
        <path d="M21 16v5h-5"/>
        <path d="M8 21H3v-5"/>
      </svg>
      <!-- Exit fullscreen: corner-in arrows -->
      <svg *ngIf="isFullscreen" width="20" height="20" viewBox="0 0 24 24"
           fill="none" stroke="currentColor"
           stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M8 3v5H3"/>
        <path d="M21 8h-5V3"/>
        <path d="M16 21v-5h5"/>
        <path d="M3 16h5v5"/>
      </svg>
    </button>

    <!-- Onboarding overlay — auto-shown on first visit, reopened via
         the "?" button. Click backdrop or "Got it" to dismiss. -->
    <div class="how-to-play-overlay"
         *ngIf="howToPlayOpen"
         (click)="closeHowToPlay()">
      <div class="how-to-play-card" (click)="$event.stopPropagation()">
        <h3 class="how-to-play-title">
          {{ currLang === 'ar' ? 'كيفية اللعب' : 'How to Play' }}
        </h3>

        <div class="how-step">
          <div class="how-step-anim how-step-anim--swipe">
            <span class="arrow arrow-l">‹</span>
            <span class="hand">👆</span>
            <span class="arrow arrow-r">›</span>
          </div>
          <div class="how-step-text">
            <strong>1. {{ currLang === 'ar' ? 'حدد الموقع' : 'Position the ball' }}</strong>
            <span>{{ currLang === 'ar' ? 'اسحب يمين أو يسار لمحاذاة الكرة على المسار.' : 'Swipe left or right to align the ball on the lane.' }}</span>
          </div>
        </div>

        <div class="how-step">
          <div class="how-step-anim how-step-anim--tap">
            <span class="ripple"></span>
            <span class="hand">👆</span>
          </div>
          <div class="how-step-text">
            <strong>2. {{ currLang === 'ar' ? 'اضغط مطولًا للقوة' : 'Hold to power up' }}</strong>
            <span>{{ currLang === 'ar' ? 'اضغط مطولًا — يمتلئ شريط القوة كلما أبقيت إصبعك مضغوطًا.' : 'Tap and hold — the power bar fills the longer you press.' }}</span>
          </div>
        </div>

        <div class="how-step">
          <div class="how-step-anim how-step-anim--bar">
            <div class="mini-bar">
              <div class="mini-bar-sweet"></div>
              <div class="mini-bar-fill"></div>
            </div>
          </div>
          <div class="how-step-text">
            <strong>3. {{ currLang === 'ar' ? 'اضرب البقعة الحلوة' : 'Hit the sweet spot' }}</strong>
            <span>{{ currLang === 'ar' ? 'اترك في المنطقة الذهبية للحصول على رمية مثالية. الإفراط في الشحن = خطأ ضعيف.' : 'Release in the gold zone for a perfect shot. Overcharging = weak foul throw.' }}</span>
          </div>
        </div>

        <button class="how-to-play-got-it"
                type="button"
                (click)="closeHowToPlay()">
          {{ currLang === 'ar' ? 'فهمت!' : 'Got it!' }}
        </button>
      </div>
    </div>
  </div>
</div>
